import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import AppBarComponent from "../components/AppBarComponent";
import MoleculeStructure from "../components/MoleculeStructure/MoleculeStructure";
import CopyableText from "../components/CopyableText";
import DownloadButton from "../components/DownloadButton";
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  Skeleton,
  Box,
  LinearProgress,
  Grid,
  Container,
  Tab,
  Tabs,
} from "@mui/material";

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const a11yProps = (index) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

const CompatibilityRunPage = () => {
  const {id: runId} = useParams();
  const [state, setState] = useState({
    sequence: null,
    smilesData: null,
    statusData: [],
    overallStatus: "",
    tabValue: 0,
    compatibilityResults: null,
    compatibilityScore: null,
  });

  const POLLING_INTERVAL = 500; // 0.5 seconds

  useEffect(() => {
    const fetchCompatibilityData = async () => {
      try {
        const response = await fetch(`/api/compatibility/${runId}`);
        const result = await response.json();

        // Prepare status steps based on job metadata
        const statusSteps = [
          {
            name: "Step 1: MSA Analysis",
            description: "Aligning your enzyme sequence with our database",
            status: result.msa_completed ? "complete" : 
                   result.job_status === "started" ? "in_progress" : "pending"
          },
          {
            name: "Step 2: Enzyme Retrieval",
            description: "Finding similar enzymes in the database",
            status: result.retrieval_completed ? "complete" : 
                   result.msa_completed ? "in_progress" : "pending",
            table: result.retrieval_results || null
          },
          {
            name: "Step 3: Substrate Analysis",
            description: "Analyzing substrate compatibility",
            status: result.substrate_analysis_completed ? "complete" : 
                   result.retrieval_completed ? "in_progress" : "pending"
          },
          {
            name: "Step 4: Scoring & Ranking",
            description: "Calculating final compatibility scores",
            status: result.reranking_completed ? "complete" : 
                   result.substrate_analysis_completed ? "in_progress" : "pending",
            table: result.reranking_results || null
          }
        ];

        setState(prevState => ({
          ...prevState,
          sequence: result.sequence,
          smilesData: result.substrate,
          statusData: statusSteps,
          overallStatus: result.analysis_completed ? "done" : "processing",
          compatibilityResults: result.compatibility_results || null,
          compatibilityScore: result.compatibility_score || null,
        }));

      } catch (error) {
        console.error("Failed to fetch compatibility data:", error);
      }
    };

    fetchCompatibilityData();

    const interval = setInterval(() => {
      if (state.overallStatus === "done") {
        clearInterval(interval);
      } else {
        fetchCompatibilityData();
      }
    }, POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [runId, state.overallStatus]);

  const renderProgress = (step) => {
    const progressProps = {
      variant: "determinate",
      color: "success",
      value: 100,
    };

    switch (step.status) {
      case "in_progress":
        if (step.progress >= 0) {
          progressProps.value = step.progress;
        } else {
          delete progressProps.value;
          delete progressProps.variant;
        }
        break;
      case "pending":
        progressProps.color = "inherit";
        delete progressProps.value;
        delete progressProps.variant;
        break;
      case "error":
        progressProps.color = "error";
        break;
      default:
        break;
    }

    return <LinearProgress {...progressProps} />;
  };

  const renderStep = (step, index) => (
    <Grid item xs={3} key={index}>
      <Typography variant="h6" sx={{marginTop: 3}}>
        <b>{step.name}</b>
      </Typography>
      <Typography variant="body1" sx={{marginBottom: 1}}>
        {step.description}
      </Typography>
      {renderProgress(step)}
      <br/>
      {step.table && step.status === "complete" ? (
        <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {Object.keys(step.table[0] || {}).map((key) => (
                  <TableCell key={key}>{key}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {step.table.map((row, i) => (
                <TableRow key={i}>
                  {Object.entries(row).map(([key, value]) => (
                    <TableCell key={key}>{value}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        step.status === "complete" && step.name === "Step 4" ? 
        (<DownloadButton runId={runId}/>) : 
        (<Skeleton variant="rectangular" width="100%" animation="wave" height={200}/>)
      )}
    </Grid>
  );

  const handleTabChange = (event, newValue) => {
    setState((prevState) => ({ ...prevState, tabValue: newValue }));
  };

  const renderCompatibilityScore = () => {
    if (state.compatibilityScore === null) return null;
    
    // Convert to percentage and determine color
    const percentScore = Math.round(state.compatibilityScore * 100);
    let scoreColor = '#f44336'; // Red for low scores
    
    if (percentScore > 70) {
      scoreColor = '#4caf50'; // Green for high scores
    } else if (percentScore > 40) {
      scoreColor = '#ff9800'; // Orange for medium scores
    }
    
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        height: '100%'
      }}>
        <Typography variant="h4" sx={{ marginBottom: 2 }}>
          Compatibility Score
        </Typography>
        <Typography variant="h2" sx={{ color: scoreColor }}>
          {percentScore}%
        </Typography>
      </Box>
    );
  };

  return (
    <div>
      <AppBarComponent/>
      <Container maxWidth="xl" sx={{marginTop: "128px"}}>
        <Grid container spacing={8}>
          <Grid item xs={6}>
            <Typography variant="h5">
              Enzyme-Substrate Compatibility Analysis
            </Typography>
            <Grid container spacing={2} sx={{ marginTop: 2 }}>
              <Grid item xs={6}>
                <Typography variant="h6">Substrate Structure</Typography>
                {state.smilesData ? (
                  <MoleculeStructure
                    id={runId}
                    structure={state.smilesData}
                    height={200}
                    width={200}
                    svgMode
                  />
                ) : (
                  <Skeleton variant="rectangular" width="100%" height={200} />
                )}
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6">Enzyme Sequence</Typography>
                {state.sequence ? (
                  <Box 
                    sx={{ 
                      maxHeight: '150px', 
                      overflow: 'auto', 
                      fontFamily: 'monospace',
                      bgcolor: '#f5f5f5',
                      p: 2,
                      borderRadius: 1
                    }}
                  >
                    {state.sequence}
                  </Box>
                ) : (
                  <Skeleton variant="rectangular" width="100%" height={150} />
                )}
              </Grid>
            </Grid>
            <Typography variant="h6" sx={{marginTop: 3, marginBottom: 1}}>
              Run ID: <CopyableText text={runId}/>
            </Typography>
            <Typography variant="body1" sx={{marginBottom: 3}}>
              Note that the results will be stored only for 7 days. Please
              save the run ID to access the results later.
            </Typography>
          </Grid>
          <Grid item xs={6}>
            {renderCompatibilityScore()}
          </Grid>
        </Grid>
      </Container>
      <hr/>
      <Container maxWidth="2560px" sx={{marginBottom: 3}}>
        <Grid container spacing={8}>
          {state.statusData.map(renderStep)}
        </Grid>
      </Container>
      <Box sx={{backgroundColor: "black", color: "white", paddingTop: "64px", paddingBottom: "64px"}}>
        <Container maxWidth="xl">
          <Tabs value={state.tabValue} onChange={handleTabChange} aria-label="results tabs" sx={{backgroundColor: "white"}}>
            <Tab label="Compatibility Details" {...a11yProps(0)} />
          </Tabs>
          <TabPanel value={state.tabValue} index={0}>
            <Typography variant="h6" sx={{color: "white", marginBottom: "24px"}}>
              Detailed Compatibility Analysis
            </Typography>
            {state.compatibilityResults ? (
              <TableContainer component={Paper} sx={{maxHeight: 600}}>
                <Table stickyHeader aria-label="compatibility table">
                                  <TableHead>
                    <TableRow>
                      {state.compatibilityResults.length > 0 && 
                        Object.keys(state.compatibilityResults[0]).map((key) => (
                          <TableCell key={key}>{key}</TableCell>
                        ))
                      }
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {state.compatibilityResults.map((result, index) => (
                      <TableRow key={index}>
                        {Object.entries(result).map(([key, value]) => (
                          <TableCell key={key}>{value}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
</Table>
              </TableContainer>
            ) : (
              state.overallStatus === "done" ? (
                <Typography variant="body1" sx={{color: "white"}}>
                  No detailed compatibility results available.
                </Typography>
              ) : (
                <Skeleton variant="rectangular" width="100%" height={400} sx={{bgcolor: 'rgba(255, 255, 255, 0.1)'}}/>
              )
            )}
          </TabPanel>
        </Container>
      </Box>
    </div>
  );
};

export default CompatibilityRunPage; 
